import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useUpload } from '../context/UploadContext';
import { useDropzone } from 'react-dropzone';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, onSnapshot, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { factUpdateArgs, markAllVerifiedArgs } from './editcase/factPaths';
import { db } from '../firebase/firebase';
import { notify } from '../utils/notifications';
import groupFacts from '../utils/groupFacts';
import useWorkflowStage from '../hooks/useWorkflowStage';
import { useUpdateAdmissionStatus } from '../hooks/useUpdateAdmissionStatus';
import WorkflowStepper from './editcase/WorkflowStepper';
import RedFlagsAlert from './editcase/RedFlagsAlert';
import RiskScoreBanner from './editcase/RiskScoreBanner';
import FactsPanel from './editcase/FactsPanel';
import UploadProgressPanel from './editcase/UploadProgressPanel';
import VerificationBar from './editcase/VerificationSidebar';
import HPIContent from './editcase/HPIContent';
import DiagnosisContent from './editcase/DiagnosisContent';
import { getDefaultTabKey } from './editcase/reviewTabs';
import DiagnosisCodesPanel from './editcase/DiagnosisCodesPanel';
import CodeSearchModal from './editcase/CodeSearchModal';
import { buildPhysicianQuery } from './editcase/physicianQuery';
import '../styles/EditCase.css';

function EditCase() {
    const { caseId } = useParams();
    const navigate = useNavigate();
    const [answer, setAnswer] = useState("");
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [diagnosis, setDiagnosis] = useState("");
    const { user, userProfile, isAdmin } = useAuth();
    const uploadCtx = useUpload();
    const [activeTab, setActiveTab] = useState("hpi");
    const [structuredInfo, setStructuredInfo] = useState(null);
    const [editableAnswers, setEditableAnswers] = useState({});
    const [copySuccess, setCopySuccess] = useState("");
    const [copyDiagnosisSuccess, setCopyDiagnosisSuccess] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatingStartTime, setGeneratingStartTime] = useState(null);
    const [processingTimedOut, setProcessingTimedOut] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [admissionStatus, setAdmissionStatus] = useState("In progress");
    const [isArchived, setIsArchived] = useState(false);
    const [extractionProgress, setExtractionProgress] = useState(null);
    const [redFlags, setRedFlags] = useState(null);
    const [diagnosisCodes, setDiagnosisCodes] = useState(null);
    const [copyDxCodesSuccess, setCopyDxCodesSuccess] = useState(false);
    // Code search/refine modal target:
    // null | {mode:'refine', code, index} | {mode:'lookup'}
    const [codeSearchTarget, setCodeSearchTarget] = useState(null);
    // code -> lookup client_id for this session's lookup evidence checks
    const [lookupClientIds, setLookupClientIds] = useState({});
    // client_ids whose evidence search has run out its client-side clock
    const [evidenceTimeouts, setEvidenceTimeouts] = useState({});
    const evidenceTimersRef = useRef({});
    const [copiedQueryIndex, setCopiedQueryIndex] = useState(null);
    const [riskScore, setRiskScore] = useState(null);
    const [recommendation, setRecommendation] = useState(null);
    const [facilityType, setFacilityType] = useState(null);

    const [generatedDocuments, setGeneratedDocuments] = useState(null);
    const [documentOrder, setDocumentOrder] = useState(null);
    // Which generated document tab just copied (drives its button's "Copied!" state)
    const [copiedDocName, setCopiedDocName] = useState(null);

    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [expandedCitations, setExpandedCitations] = useState({});

    const [documentsStale, setDocumentsStale] = useState(false);
    const [resultsStale, setResultsStale] = useState(false);
    const [expandedFileNames, setExpandedFileNames] = useState(new Set());
    const [showReExtractConfirm, setShowReExtractConfirm] = useState(false);
    const [showReGenerateConfirm, setShowReGenerateConfirm] = useState(false);
    // Tracks whether a fresh extraction is in flight (so we know when to clear resultsStale)
    const pendingExtraction = useRef(false);
    // Guards the one-time UploadContext.clear() below so it can't loop.
    const uploadCtxCleared = useRef(false);
    // Re-arm the guard whenever the viewed case changes, so clear() can
    // still fire the *next* time this same case's extraction appears.
    useEffect(() => {
        uploadCtxCleared.current = false;
    }, [caseId]);

    // Once extraction is underway (progress or results present) FOR THE
    // CASE CURRENTLY BEING VIEWED, the upload panel's job is done — clear
    // UploadContext so it stops rendering and the case's own
    // Firestore-backed file list takes over.
    //
    // Critically gated on uploadCtx.caseId === caseId: UploadContext is a
    // single, app-wide upload engine, so a background upload for case A can
    // still be in flight while the user navigates to and views an unrelated
    // case B that already has extraction results. Without this check,
    // opening B would clear() UploadContext — aborting A's in-flight PUTs
    // and silently killing that upload.
    useEffect(() => {
        if (uploadCtxCleared.current) return;
        if (uploadCtx.caseId !== caseId) return;
        if (extractionProgress || structuredInfo) {
            uploadCtxCleared.current = true;
            uploadCtx.clear();
        }
    }, [caseId, extractionProgress, structuredInfo, uploadCtx]);

    // Data-driven stage (furthest progress reached)
    const { stage: workflowStage } = useWorkflowStage({
        structuredInfo,
        extractionProgress,
        hpiText: answer,
        diagnosis,
        generatedDocuments,
    });

    // User-controlled viewing stage (null = follow data-driven stage)
    const [viewingStage, setViewingStage] = useState(null);
    const activeStage = viewingStage ?? workflowStage;

    // Home-health diagnosis codes are computed at upload-time processing and
    // reviewed in their own Coding step between Review & Approve and
    // Documents. The step exists and is clickable as soon as codes do, before
    // fact verification or document generation. The data-driven workflowStage
    // still decides where the user lands by default, so fact review stays the
    // current stage until generation.
    const showCodingStep = facilityType === 'home_health' && !!diagnosisCodes;

    // Per-step reachability for the stepper. The ordinal stages unlock with
    // data-driven progress (as before); Coding unlocks with the codes
    // themselves, independent of stage progress.
    const reachableStages = useMemo(() => {
        const reachable = new Set(['upload']);
        if (workflowStage === 'results' || workflowStage === 'documents') {
            reachable.add('results');
        }
        if (workflowStage === 'documents') {
            reachable.add('documents');
        }
        if (showCodingStep) {
            reachable.add('coding');
        }
        return reachable;
    }, [workflowStage, showCodingStep]);

    // When the data-driven stage advances, reset viewing override so user sees the new stage
    const prevWorkflowStageRef = useRef(null);
    useEffect(() => {
        if (prevWorkflowStageRef.current !== null && workflowStage !== prevWorkflowStageRef.current) {
            setViewingStage(null);
        }
        prevWorkflowStageRef.current = workflowStage;
    }, [workflowStage]);

    const handleStageClick = (stageKey) => {
        if (stageKey === workflowStage) {
            // Clicking the current progress stage clears the override
            setViewingStage(null);
        } else {
            setViewingStage(stageKey);
        }
    };

    // Sort document names by facility config order (Firestore maps lose insertion order)
    const sortedDocNames = useMemo(() => {
        if (!generatedDocuments) return [];
        const keys = Object.keys(generatedDocuments);
        if (!documentOrder) return keys;
        return keys.sort((a, b) => {
            const ai = documentOrder.indexOf(a);
            const bi = documentOrder.indexOf(b);
            return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        });
    }, [generatedDocuments, documentOrder]);

    // When generatedDocuments arrives, initialize activeTab to the default
    // document. Every Firestore snapshot gives sortedDocNames a fresh array
    // identity, so this effect re-runs on every write to the case; it must
    // leave activeTab alone whenever the current tab is still valid, or any
    // save (fact edit, admission status change) kicks the user back to the
    // default tab.
    useEffect(() => {
        if (sortedDocNames.length === 0) return;
        setActiveTab(prev => {
            if (sortedDocNames.includes(prev)) return prev;
            // Persona-aware default. Role plumbing is deferred (see plan Task
            // 13); callers pass null until facility membership/role data is
            // exposed.
            const preferred = getDefaultTabKey(facilityType, null);
            return sortedDocNames.includes(preferred) ? preferred : sortedDocNames[0];
        });
    }, [sortedDocNames, facilityType]);

    // Processing timeout configuration (10 minutes)
    const PROCESSING_TIMEOUT_MS = 10 * 60 * 1000;

    const toggleGroup = (groupName) => {
        setCollapsedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
    };

    const toggleCitation = (fieldKey) => {
        setExpandedCitations(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
    };

    const groupedFacts = useMemo(() => groupFacts(structuredInfo), [structuredInfo]);
    const prevGroupedFactsRef = useRef([]);

    // Auto-collapse groups when all their facts are verified.
    // On initial load, collapse already-verified groups immediately.
    // On subsequent updates, collapse newly-completed groups after 300ms,
    // unless ALL groups completed at once ("Mark All Correct").
    useEffect(() => {
        if (groupedFacts.length === 0) return;

        const prevGroups = prevGroupedFactsRef.current;
        const isInitialLoad = prevGroups.length === 0;
        prevGroupedFactsRef.current = groupedFacts;

        if (isInitialLoad) {
            // Collapse any groups that are already fully verified when case loads
            const toCollapse = {};
            for (const group of groupedFacts) {
                if (group.verifiedCount === group.totalCount && group.totalCount > 0) {
                    toCollapse[group.name] = true;
                }
            }
            if (Object.keys(toCollapse).length > 0) {
                setCollapsedGroups(prev => ({ ...prev, ...toCollapse }));
            }
            return;
        }

        // Skip auto-collapse when ALL groups are complete (e.g. "Mark All Correct")
        const allComplete = groupedFacts.every(g => g.verifiedCount === g.totalCount && g.totalCount > 0);
        if (allComplete) return;

        const timeouts = [];
        for (const group of groupedFacts) {
            if (group.verifiedCount === group.totalCount && group.totalCount > 0 && !collapsedGroups[group.name]) {
                const id = setTimeout(() => {
                    setCollapsedGroups(prev => {
                        if (prev[group.name]) return prev;
                        return { ...prev, [group.name]: true };
                    });
                }, 300);
                timeouts.push(id);
            }
        }
        return () => timeouts.forEach(clearTimeout);
    }, [groupedFacts]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load case data if editing an existing case
    useEffect(() => {
        const loadCaseData = async () => {
            // Reset state for both new and existing cases
            setAnswer("");
            setDiagnosis("");
            setSelectedFiles([]);
            setStructuredInfo(null);
            setEditableAnswers({});
            setCopySuccess("");
            setCopyDiagnosisSuccess("");

            // If caseId is undefined or 'new', or no user, we're done
            if (!caseId || caseId === 'new' || !user?.uid) {
                return;
            }

            // Only try to load existing case data if we have a valid caseId
            try {
                const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
                const caseDoc = await getDoc(caseRef);

                if (caseDoc.exists()) {
                    const caseData = caseDoc.data();
                    // Set the structured info from the stored data
                    if (caseData.structuredInfo) {
                        setStructuredInfo(caseData.structuredInfo);
                    }
                } else {
                    console.error('Case not found');
                    navigate('/cases');
                }
            } catch (error) {
                console.error('Error loading case:', error);
                navigate('/cases');
            }
        };

        loadCaseData();
    }, [caseId, navigate, user?.uid]);

    useEffect(() => {
        // Subscribe to real-time updates for the case
        if (!caseId || caseId === 'new' || !user?.uid) {
            return;
        }

        const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
        const unsubscribe = onSnapshot(caseRef, (doc) => {
            if (doc.exists()) {
                const caseData = doc.data();
                // Update the UI with the latest data
                if (caseData.structuredInfo) {
                    setStructuredInfo(caseData.structuredInfo);
                }
                if (caseData.hpi) {
                    setAnswer(prev => {
                        // If HPI content changed (fresh generation), clear stale flag
                        if (caseData.hpi !== prev) setDocumentsStale(false);
                        return caseData.hpi;
                    });
                }
                if (caseData.diagnosis) {
                    setDiagnosis(caseData.diagnosis);
                }
                if (caseData.generated_documents) {
                    setGeneratedDocuments(caseData.generated_documents);
                }
                if (caseData.document_order) {
                    setDocumentOrder(caseData.document_order);
                }
                if (caseData.files) {
                    setUploadedFiles(caseData.files);
                }
                if (caseData.admission_status) {
                    setAdmissionStatus(caseData.admission_status);
                }
                setIsArchived(caseData.archived === true);
                // Update extraction progress (for progressive field extraction)
                if (caseData.extraction_progress) {
                    setExtractionProgress(caseData.extraction_progress);
                } else {
                    setExtractionProgress(null);
                }
                // Update red flags
                if (caseData.redFlags) {
                    setRedFlags(caseData.redFlags);
                }
                // Update diagnosis codes (home_health cases only)
                setDiagnosisCodes(caseData.diagnosisCodes ?? null);
                setRiskScore(caseData.risk_score ?? null);
                setRecommendation(caseData.recommendation ?? null);
                setFacilityType(caseData.facility_type ?? null);
                // Update case completion status

                // Update generation status: only turn OFF generating when
                // status explicitly transitions to success/error (avoids
                // flicker from snapshots that arrive before backend sets 'working')
                const statusVal = caseData.status;
                const wasGenerating = isGenerating;
                const isWorking = !!statusVal && typeof statusVal === 'object' && statusVal.condition === 'working';
                const isDone = !!statusVal && typeof statusVal === 'object' &&
                    (statusVal.condition === 'success' || statusVal.condition === 'error');

                if (isWorking && !wasGenerating) {
                    setIsGenerating(true);
                    setGeneratingStartTime(Date.now());
                    setProcessingTimedOut(false);
                } else if (isDone && wasGenerating) {
                    setIsGenerating(false);
                    setGeneratingStartTime(null);
                    setProcessingTimedOut(false);
                    // If a re-extraction was pending, clear staleness now that it completed
                    if (pendingExtraction.current) {
                        pendingExtraction.current = false;
                        setResultsStale(false);
                        setDocumentsStale(false);
                    }
                }

                // Check for error status
                if (statusVal && statusVal.condition === 'error') {
                    console.error('Processing error:', statusVal.message);
                    notify.error(`Processing failed: ${statusVal.message}`);
                }
            }
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, [caseId, user?.uid, isGenerating]);

    // Timeout handling for stuck processing
    useEffect(() => {
        if (!isGenerating || !generatingStartTime) {
            return;
        }

        const checkTimeout = () => {
            const elapsed = Date.now() - generatingStartTime;
            if (elapsed >= PROCESSING_TIMEOUT_MS) {
                console.warn('Processing timeout detected after', Math.round(elapsed / 1000), 'seconds');
                setProcessingTimedOut(true);
            }
        };

        // Check immediately in case we're already past timeout
        checkTimeout();

        // Set up interval to check periodically
        const intervalId = setInterval(checkTimeout, 30000); // Check every 30 seconds

        return () => clearInterval(intervalId);
    }, [isGenerating, generatingStartTime, PROCESSING_TIMEOUT_MS]);

    const onDrop = useCallback((acceptedFiles) => {
        setSelectedFiles(acceptedFiles);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
        },
        multiple: true
    });

    const handleFileUpload = (e) => {
        e.preventDefault();
        if (!selectedFiles.length) return;

        // Single upload path: hand off to UploadContext so these files get
        // the same progress/stall/retry handling as a brand-new case's
        // uploads. autoProcess:false because this flow always ends with the
        // user manually clicking Extract/Re-extract — never an automatic
        // process-case call (unlike NewCase's initial upload).
        uploadCtx.addFilesToCase(caseId, selectedFiles, { autoProcess: false });

        // Downstream stages are now stale — source documents changed. Set
        // optimistically since addFilesToCase uploads in the background and
        // doesn't resolve per-file completion back to the caller.
        setResultsStale(true);
        setDocumentsStale(true);

        setSelectedFiles([]);
    };

    const handleDeleteFile = async (file) => {
        if (!user?.uid || !caseId) return;

        // Remove from local state immediately
        setUploadedFiles(prev => prev.filter(f => f.path !== file.path));

        // Downstream stages are now stale — source documents changed
        setResultsStale(true);
        setDocumentsStale(true);

        // Remove from Firestore
        try {
            const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
            const caseDoc = await getDoc(caseRef);
            const currentFiles = caseDoc.data()?.files || [];
            const updatedFiles = currentFiles.filter(f => f.path !== file.path);
            await updateDoc(caseRef, {
                files: updatedFiles,
                last_modified_date: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error deleting file:', error);
            notify.error('Failed to delete file');
            // Revert local state
            setUploadedFiles(prev => [...prev, file]);
        }
    };

    const handleProcessCase = async ({ confirm = false } = {}) => {
        if (!user?.uid || !caseId || isArchived) return;

        // If there are existing results, confirm before wiping them
        const hasExistingResults = !!(structuredInfo || extractionProgress);
        if (hasExistingResults && !confirm) {
            setShowReExtractConfirm(true);
            return;
        }
        setShowReExtractConfirm(false);

        // Clear existing extraction results and downstream content locally
        setStructuredInfo(null);
        setExtractionProgress(null);
        setEditableAnswers({});
        setRedFlags(null);
        setDiagnosisCodes(null);
        setAnswer("");
        setDiagnosis("");
        setCollapsedGroups({});
        setExpandedCitations({});

        // Clear stale data in Firestore so onSnapshot doesn't repopulate old values
        try {
            const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
            await updateDoc(caseRef, {
                structuredInfo: deleteField(),
                extraction_progress: deleteField(),
                hpi: deleteField(),
                diagnosis: deleteField(),
                redFlags: deleteField(),
                diagnosisCodes: deleteField(),
                last_modified_date: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error clearing Firestore fields:', error);
        }

        // Track that we're awaiting fresh extraction results
        pendingExtraction.current = true;

        try {
            const formData = new FormData();
            formData.append('case_id', caseId);

            await api.post('/case/process-case', formData);
        } catch (error) {
            console.error('Error starting extraction:', error);
            notify.error('Failed to start extraction. Please try again.');
            pendingExtraction.current = false;
        }
    };

    const handleGenerateAll = async ({ confirm = false } = {}) => {
        if (!user?.uid || !caseId || isArchived) return;

        // If existing documents exist, confirm before wiping them
        const hasExistingDocs = !!(answer || diagnosis);
        if (hasExistingDocs && !confirm) {
            setShowReGenerateConfirm(true);
            return;
        }
        setShowReGenerateConfirm(false);

        // Clear existing documents locally
        setAnswer("");
        setDiagnosis("");

        // Clear stale documents in Firestore so onSnapshot doesn't repopulate old values
        try {
            const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
            await updateDoc(caseRef, {
                hpi: deleteField(),
                diagnosis: deleteField(),
                last_modified_date: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error clearing Firestore fields:', error);
        }

        // Immediately reflect generating state in UI; Firestore will keep it updated
        setIsGenerating(true);
        try {
            const formData = new FormData();
            formData.append('case_id', caseId);

            await api.post('/case/generate_initial_content', formData);
        } catch (error) {
            console.error('Error generating content:', error);
            const errorMessage = error.response?.data?.details || error.message;
            notify.error(`Failed to generate content: ${errorMessage}`);
            setIsGenerating(false);
        }
    };

    const { change: changeAdmissionStatus } = useUpdateAdmissionStatus({
        caseId,
        currentStatus: admissionStatus,
        onOptimisticChange: setAdmissionStatus,
    });

    const handleAdmissionStatusChange = (e) => changeAdmissionStatus(e.target.value);

    const handleFactVerification = async (field, isCorrect, e) => {
        e.preventDefault();

        if (!user?.uid || !caseId) return;

        // If HPI exists and a fact changes, downstream documents are stale
        if (answer) setDocumentsStale(true);

        try {
            // Update local state immediately for better UX
            setStructuredInfo(prev => ({
                ...prev,
                [field]: {
                    ...prev[field],
                    verified: isCorrect
                }
            }));

            // Update Firebase
            const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
            await updateDoc(caseRef, ...factUpdateArgs(field, 'verified', isCorrect));

            // If "Modify" is clicked or "Correct" is unchecked (isCorrect is false), make the field editable
            if (!isCorrect) {
                setEditableAnswers(prev => ({
                    ...prev,
                    [field]: structuredInfo[field].answer
                }));
            } else {
                // If "Correct" is clicked, remove the field from editableAnswers
                setEditableAnswers(prev => {
                    const newState = { ...prev };
                    delete newState[field];
                    return newState;
                });
            }
        } catch (error) {
            console.error('Error updating verification state:', error);
            // Revert local state if Firebase update fails
            setStructuredInfo(prev => ({
                ...prev,
                [field]: {
                    ...prev[field],
                    verified: !isCorrect
                }
            }));
        }
    };

    const handleRedFlagReview = async (index, newStatus) => {
        if (!user?.uid || !caseId || !redFlags) return;

        const prevFlags = redFlags.flags;
        const updatedFlags = prevFlags.map((flag, i) => {
            if (i !== index) return flag;
            // Toggle: if already this status, revert to pending
            const status = flag.status === newStatus ? 'pending' : newStatus;
            return { ...flag, status };
        });

        setRedFlags(prev => ({ ...prev, flags: updatedFlags }));

        try {
            const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
            await updateDoc(caseRef, {
                'redFlags.flags': updatedFlags,
                last_modified_date: serverTimestamp()
            });
        } catch (error) {
            console.error('Error updating red flag status:', error);
            setRedFlags(prev => ({ ...prev, flags: prevFlags }));
        }
    };

    const handleMarkAllRedFlagsReviewed = async () => {
        if (!user?.uid || !caseId || !redFlags) return;

        const prevFlags = redFlags.flags;
        const updatedFlags = prevFlags.map(flag => ({ ...flag, status: 'approved' }));

        setRedFlags(prev => ({ ...prev, flags: updatedFlags }));

        try {
            const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
            await updateDoc(caseRef, {
                'redFlags.flags': updatedFlags,
                last_modified_date: serverTimestamp()
            });
        } catch (error) {
            console.error('Error marking all red flags reviewed:', error);
            setRedFlags(prev => ({ ...prev, flags: prevFlags }));
        }
    };

    const handleDiagnosisCodeReview = async (index, newStatus) => {
        if (!user?.uid || !caseId || !diagnosisCodes) return;

        const prevCandidates = diagnosisCodes.candidates;
        const prevPrimary = diagnosisCodes.primary_index ?? null;
        const updatedCandidates = prevCandidates.map((cand, i) => {
            if (i !== index) return cand;
            // Toggle: if already this status, revert to pending
            const status = cand.status === newStatus ? 'pending' : newStatus;
            return { ...cand, status };
        });
        // A candidate leaving 'selected' loses any primary designation
        const stillSelected = updatedCandidates[prevPrimary]?.status === 'selected';
        const updatedPrimary = stillSelected ? prevPrimary : null;

        setDiagnosisCodes(prev => ({
            ...prev, candidates: updatedCandidates, primary_index: updatedPrimary,
        }));

        try {
            const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
            await updateDoc(caseRef, {
                'diagnosisCodes.candidates': updatedCandidates,
                'diagnosisCodes.primary_index': updatedPrimary,
                last_modified_date: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error updating diagnosis code status:', error);
            setDiagnosisCodes(prev => ({
                ...prev, candidates: prevCandidates, primary_index: prevPrimary,
            }));
        }
    };

    const handleDesignatePrimary = async (index) => {
        if (!user?.uid || !caseId || !diagnosisCodes) return;

        const prevPrimary = diagnosisCodes.primary_index ?? null;
        setDiagnosisCodes(prev => ({ ...prev, primary_index: index }));

        try {
            const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
            await updateDoc(caseRef, {
                'diagnosisCodes.primary_index': index,
                last_modified_date: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error designating primary diagnosis:', error);
            setDiagnosisCodes(prev => ({ ...prev, primary_index: prevPrimary }));
        }
    };

    const handleCopyGeneratedDoc = async (docName) => {
        const content = generatedDocuments?.[docName]?.content;
        if (!content) return;
        try {
            await navigator.clipboard.writeText(content);
            setCopiedDocName(docName);
            setTimeout(() => setCopiedDocName(null), 2000);
        } catch (error) {
            console.error('Error copying document:', error);
        }
    };

    const handleCopyDiagnosisCodes = async () => {
        if (!diagnosisCodes) return;
        const { candidates, primary_index: primaryIndex } = diagnosisCodes;
        const selected = candidates
            .map((cand, i) => ({ cand, i }))
            .filter(({ cand }) => cand.code && cand.status === 'selected');
        // Primary first, then remaining selected codes in list order
        selected.sort((a, b) => (a.i === primaryIndex ? -1 : b.i === primaryIndex ? 1 : 0));
        const text = selected.map(({ cand }) => cand.code).join('\n');
        try {
            await navigator.clipboard.writeText(text);
            setCopyDxCodesSuccess(true);
            setTimeout(() => setCopyDxCodesSuccess(false), 2000);
        } catch (error) {
            console.error('Error copying diagnosis codes:', error);
        }
    };

    const handleCopyPhysicianQuery = async (index) => {
        const candidate = diagnosisCodes?.candidates?.[index];
        if (!candidate) return;
        const first = structuredInfo?.['Patient First Name']?.answer;
        const last = structuredInfo?.['Patient Last Name']?.answer;
        const patientName = [first, last].filter(Boolean).join(' ');
        const text = buildPhysicianQuery(candidate, patientName ? { patientName } : {});
        try {
            await navigator.clipboard.writeText(text);
            setCopiedQueryIndex(index);
            setTimeout(() => setCopiedQueryIndex(null), 2000);
        } catch (error) {
            console.error('Error copying physician query:', error);
        }
    };

    /* ── User-driven code search / refine / evidence lookup ── */

    // Server evidence results land at diagnosisCodes.userEvidence.{client_id}
    // (disjoint from the client-owned candidates array); overlay them onto
    // the matching candidates at render time.
    const mergedDiagnosisCodes = useMemo(() => {
        if (!diagnosisCodes) return null;
        const userEvidence = diagnosisCodes.userEvidence || {};
        const candidates = (diagnosisCodes.candidates || []).map(cand => {
            if (!cand.client_id) return cand;
            const result = userEvidence[cand.client_id];
            if (!result) {
                return evidenceTimeouts[cand.client_id]
                    ? { ...cand, evidence_status: 'timeout' }
                    : cand;
            }
            if (result.status !== 'found') return { ...cand, evidence_status: result.status };
            return {
                ...cand,
                evidence_status: 'found',
                evidence: result.evidence,
                page: result.page,
                document: result.document,
            };
        });
        return { ...diagnosisCodes, candidates };
    }, [diagnosisCodes, evidenceTimeouts]);

    // Evidence results for lookup-mode checks, keyed by code. Lookup checks
    // reuse the userEvidence mechanism with a lookup- prefixed client_id and
    // no candidate row, so the panel merge never sees them.
    const lookupEvidence = useMemo(() => {
        const userEvidence = diagnosisCodes?.userEvidence || {};
        return Object.fromEntries(
            Object.entries(lookupClientIds).map(([code, clientId]) => {
                const result = userEvidence[clientId];
                if (result) return [code, result];
                return [code, { status: evidenceTimeouts[clientId] ? 'timeout' : 'pending' }];
            })
        );
    }, [lookupClientIds, diagnosisCodes, evidenceTimeouts]);

    const startEvidenceClock = useCallback((clientId) => {
        if (evidenceTimersRef.current[clientId]) {
            clearTimeout(evidenceTimersRef.current[clientId]);
        }
        evidenceTimersRef.current[clientId] = setTimeout(() => {
            delete evidenceTimersRef.current[clientId];
            setEvidenceTimeouts(prev => ({ ...prev, [clientId]: true }));
        }, 90000);
    }, []);

    // Clear timeout clocks (and flags) for candidates whose evidence arrived,
    // and start a clock for orphaned pendings — a persisted 'pending' with no
    // userEvidence entry and no running clock means the page was reloaded (or
    // the background task died) mid-search; without this it would spin forever
    // with no path to Retry.
    useEffect(() => {
        const userEvidence = diagnosisCodes?.userEvidence || {};
        Object.keys(userEvidence).forEach(clientId => {
            if (evidenceTimersRef.current[clientId]) {
                clearTimeout(evidenceTimersRef.current[clientId]);
                delete evidenceTimersRef.current[clientId];
            }
        });
        setEvidenceTimeouts(prev => {
            const next = Object.fromEntries(
                Object.entries(prev).filter(([clientId]) => !userEvidence[clientId])
            );
            return Object.keys(next).length === Object.keys(prev).length ? prev : next;
        });
        (diagnosisCodes?.candidates || []).forEach(cand => {
            if (
                cand.client_id &&
                cand.evidence_status === 'pending' &&
                !userEvidence[cand.client_id] &&
                !evidenceTimersRef.current[cand.client_id] &&
                !evidenceTimeouts[cand.client_id]
            ) {
                startEvidenceClock(cand.client_id);
            }
        });
    }, [diagnosisCodes, evidenceTimeouts, startEvidenceClock]);

    useEffect(() => () => {
        Object.values(evidenceTimersRef.current).forEach(clearTimeout);
    }, []);

    const fireEvidenceSearch = (code, clientId) => {
        setEvidenceTimeouts(prev => {
            if (!prev[clientId]) return prev;
            const { [clientId]: _removed, ...rest } = prev;
            return rest;
        });
        const formData = new FormData();
        formData.append('case_id', caseId);
        formData.append('code', code);
        formData.append('client_id', clientId);
        api.post('/case/find-code-evidence', formData).catch(error => {
            console.error('Error starting evidence search:', error);
            setEvidenceTimeouts(prev => ({ ...prev, [clientId]: true }));
        });
        startEvidenceClock(clientId);
    };

    // Table facts arrive with the search/browse hit, so the candidate is
    // stamped client-side with no extra roundtrip.
    const buildUserCandidate = (hit, clientId) => ({
        code: hit.code,
        rationale: '',
        evidence: '',
        page: '',
        document: '',
        missing_fact: null,
        validation: {
            valid: hit.billable,
            description: hit.description,
            acceptable_primary: hit.pdgm?.acceptable_primary ?? false,
            clinical_group: hit.pdgm?.clinical_group ?? null,
            comorbidity_subgroup: hit.pdgm?.comorbidity_subgroup ?? null,
        },
        verification: null,
        audit_flags: [],
        query_options: [],
        status: 'selected',
        source: 'user',
        client_id: clientId,
        evidence_status: 'pending',
    });

    const fetchExcludes1Flags = async (newCode) => {
        const selectedCodes = diagnosisCodes.candidates
            .filter(c => c.code && c.status === 'selected' && c.code !== newCode)
            .map(c => c.code);
        if (selectedCodes.length === 0) return [];
        try {
            const resp = await api.get('/icd/excludes1-conflicts', {
                params: { codes: [...selectedCodes, newCode].join(',') },
            });
            return resp.data.conflicts
                .filter(c => c.code === newCode || c.conflicts_with === newCode)
                .map(c => `Excludes1 conflict: ${c.code} excludes ${c.conflicts_with} (${c.note})`);
        } catch (error) {
            console.error('Error checking Excludes1 conflicts:', error);
            return [];
        }
    };

    const handleCodeSearchSelect = async (hit) => {
        const target = codeSearchTarget;
        setCodeSearchTarget(null);
        if (!user?.uid || !caseId || !diagnosisCodes || !target) return;
        if (target.mode === 'refine' && diagnosisCodes.candidates[target.index]?.code === hit.code) {
            return; // picked the same code — nothing to change
        }
        // A code already on the list (and not dismissed) must not be added
        // twice — the copy list would emit it twice.
        const duplicate = diagnosisCodes.candidates.some(
            (cand, i) =>
                cand.code === hit.code &&
                cand.status !== 'rejected' &&
                !(target.mode === 'refine' && i === target.index)
        );
        if (duplicate) {
            notify.info(`${hit.code} is already on the code list.`);
            return;
        }

        // A lookup evidence check may already have run (or be running) for
        // this code; reusing its client_id lets the merge pick up that result
        // with no second search. Guarded against a dismissed candidate that
        // still carries the id from an earlier add.
        const lookupClientId = target.mode === 'lookup' ? lookupClientIds[hit.code] : null;
        const reuseLookupId = Boolean(
            lookupClientId &&
            !diagnosisCodes.candidates.some(c => c.client_id === lookupClientId)
        );
        const clientId = reuseLookupId ? lookupClientId : crypto.randomUUID();
        const newCandidate = buildUserCandidate(hit, clientId);
        newCandidate.audit_flags = await fetchExcludes1Flags(hit.code);

        const prevCandidates = diagnosisCodes.candidates;
        const prevPrimary = diagnosisCodes.primary_index ?? null;
        let updatedCandidates;
        let updatedPrimary = prevPrimary;
        if (target.mode === 'refine') {
            // Add-new + reject-old: indices stay stable and the AI card keeps
            // its disclosures as an audit trail.
            newCandidate.refined_from = diagnosisCodes.candidates[target.index]?.code ?? null;
            updatedCandidates = prevCandidates.map((cand, i) => (
                i === target.index
                    ? { ...cand, status: 'rejected', replaced_by: clientId }
                    : cand
            ));
            updatedCandidates = [...updatedCandidates, newCandidate];
            if (prevPrimary === target.index) updatedPrimary = updatedCandidates.length - 1;
        } else {
            updatedCandidates = [...prevCandidates, newCandidate];
        }

        setDiagnosisCodes(prev => ({
            ...prev, candidates: updatedCandidates, primary_index: updatedPrimary,
        }));
        try {
            const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
            await updateDoc(caseRef, {
                'diagnosisCodes.candidates': updatedCandidates,
                'diagnosisCodes.primary_index': updatedPrimary,
                last_modified_date: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error adding diagnosis code:', error);
            notify.error('Could not add the code. Try again.');
            setDiagnosisCodes(prev => ({
                ...prev, candidates: prevCandidates, primary_index: prevPrimary,
            }));
            return;
        }
        if (!reuseLookupId) fireEvidenceSearch(hit.code, clientId);
    };

    const handleRefineCode = (index) => {
        const code = diagnosisCodes?.candidates?.[index]?.code;
        if (code) setCodeSearchTarget({ mode: 'refine', code, index });
    };

    const handleRetryEvidence = (index) => {
        const cand = diagnosisCodes?.candidates?.[index];
        if (cand?.code && cand?.client_id) fireEvidenceSearch(cand.code, cand.client_id);
    };

    const handleLookupEvidence = (code) => {
        const existing = lookupClientIds[code];
        if (existing) {
            // Retry path: same id, so the result overwrites in place.
            fireEvidenceSearch(code, existing);
            return;
        }
        const clientId = `lookup-${crypto.randomUUID()}`;
        setLookupClientIds(prev => ({ ...prev, [code]: clientId }));
        fireEvidenceSearch(code, clientId);
    };

    const handleAnswerEdit = async (field, newValue) => {
        if (!user?.uid || !caseId) return;

        // If HPI exists and a fact changes, downstream documents are stale
        if (answer) setDocumentsStale(true);

        try {
            // Update local state immediately for better UX
            setEditableAnswers(prev => ({
                ...prev,
                [field]: newValue
            }));

            setStructuredInfo(prev => ({
                ...prev,
                [field]: {
                    ...prev[field],
                    answer: newValue
                }
            }));

            // Update Firebase
            const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
            await updateDoc(caseRef, ...factUpdateArgs(field, 'answer', newValue));
        } catch (error) {
            console.error('Error updating answer:', error);
            // Revert local state if Firebase update fails
            setStructuredInfo(prev => ({
                ...prev,
                [field]: {
                    ...prev[field],
                    answer: prev[field].answer
                }
            }));
        }
    };

    // Calculate verification stats
    const getVerificationStats = () => {
        if (!structuredInfo) return null;

        const totalFacts = Object.keys(structuredInfo).length;
        const verifiedCorrect = Object.values(structuredInfo).filter(v => v.verified === true).length;

        return {
            total: totalFacts,
            verified: verifiedCorrect
        };
    };

    // Check if all facts are verified correct
    const allFactsVerifiedCorrect = () => {
        const stats = getVerificationStats();
        return stats?.verified === stats?.total && stats?.total > 0;
    };

    const handleDismissFactReviewBanner = async () => {
        try {
            await api.put('/user/profile', { fact_review_onboarding_dismissed: true });
        } catch (error) {
            console.error('Failed to dismiss fact-review banner:', error);
        }
    };

    const handleMarkAllCorrect = async () => {
        if (!user?.uid || !caseId || !structuredInfo) return;

        try {
            // Update local state for immediate feedback
            setStructuredInfo(prev => {
                if (!prev) return prev;
                const updated = {};
                Object.entries(prev).forEach(([key, value]) => {
                    updated[key] = { ...value, verified: true };
                });
                return updated;
            });

            // Clear edits since all are marked correct
            setEditableAnswers({});

            // Collapse all groups
            const collapsed = {};
            groupedFacts.forEach(g => { collapsed[g.name] = true; });
            setCollapsedGroups(collapsed);

            // Single varargs update; FieldPath segments keep admin-authored
            // fact names with reserved characters safe (see factPaths.js).
            const caseRef = doc(db, 'users', user.uid, 'cases', caseId);
            await updateDoc(caseRef, ...markAllVerifiedArgs(structuredInfo));
        } catch (error) {
            console.error('Error marking all as correct:', error);
        }
    };

    const handleCopyHPI = async () => {
        try {
            if (!navigator.clipboard) {
                // Fallback for browsers that don't support clipboard API
                const textArea = document.createElement('textarea');
                textArea.value = answer;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                setCopySuccess("Copied!");
            } else {
                await navigator.clipboard.writeText(answer);
                setCopySuccess("Copied!");
            }

            setTimeout(() => {
                setCopySuccess("");
            }, 2000);
        } catch (err) {
            console.error("Copy failed:", err);
            setCopySuccess("Failed to copy!");
            setTimeout(() => {
                setCopySuccess("");
            }, 2000);
        }
    };

    const handleCopyDiagnosis = async () => {
        try {
            if (!navigator.clipboard) {
                const textArea = document.createElement('textarea');
                textArea.value = diagnosis;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                setCopyDiagnosisSuccess("Copied!");
            } else {
                await navigator.clipboard.writeText(diagnosis);
                setCopyDiagnosisSuccess("Copied!");
            }

            setTimeout(() => {
                setCopyDiagnosisSuccess("");
            }, 2000);
        } catch (err) {
            console.error("Copy failed:", err);
            setCopyDiagnosisSuccess("Failed to copy!");
            setTimeout(() => {
                setCopyDiagnosisSuccess("");
            }, 2000);
        }
    };


    const toggleFileName = (key) => {
        setExpandedFileNames(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Upload section used in Stage 1
    const uploadSection = (
        <div className="form">
            {uploadedFiles.length > 0 && (
                <div className="uploaded-files">
                    <h3>Uploaded Files</h3>
                    <ul>
                        {uploadedFiles.map((file, index) => (
                            <li key={file.path || index} className="uploaded-file-item">
                                <span
                                    className={`file-name${expandedFileNames.has(file.path || index) ? ' expanded' : ''}`}
                                    title={file.name}
                                    onClick={() => toggleFileName(file.path || index)}
                                >{file.name}</span>
                                <span className="file-date">
                                    {new Date(file?.uploadedAt?.toDate ? file.uploadedAt.toDate() : file.uploadedAt).toLocaleString()}
                                </span>
                                <button
                                    type="button"
                                    className="delete-file-button"
                                    onClick={() => handleDeleteFile(file)}
                                >
                                    Delete
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div {...getRootProps()} className="dropzone">
                <input {...getInputProps()} />
                {isDragActive ? (
                    <p>Drop files here ...</p>
                ) : (
                    <p>Drag and drop PDF or DOCX files here, or click to select files</p>
                )}
                {selectedFiles.length > 0 && (
                    <ul className="selected-files-list">
                        {selectedFiles.map((f, i) => (
                            <li key={i} className="selected-file">{f.name}</li>
                        ))}
                    </ul>
                )}
            </div>

            <button
                className="form-button"
                type="button"
                onClick={handleFileUpload}
                disabled={!selectedFiles.length}
            >
                Upload Files
            </button>
        </div>
    );

    // While UploadContext is actively tracking files for *this* case, show
    // progress instead of the dropzone — avoids a confusing empty dropzone
    // while uploads for this case are still in flight (or need
    // retry/removal) in the background.
    //
    // Non-terminal files (pending/uploading/stalled/failed) always keep the
    // panel up. But for an `autoProcess`-eligible batch (the NewCase flow —
    // uploads that continue in the background after navigating straight to
    // EditCase), we ALSO keep the panel up once every file reaches `done`
    // but before uploadCtx.clear() fires: that clear() only happens once
    // Firestore's onSnapshot reflects extraction underway, and there's a
    // real gap between "last file finalized" and that snapshot arriving.
    // Dropping to the dropzone/Extract UI during that gap let a user fire a
    // second /case/process-case by clicking the (still-enabled) Extract
    // button, racing the context's own auto-fired call.
    const hasRelevantFiles = uploadCtx.caseId === caseId &&
        uploadCtx.files.some((f) => f.status !== 'removed');
    const uploadInProgress = uploadCtx.files.some(
        (f) => ['pending', 'uploading', 'stalled', 'failed'].includes(f.status)
    );
    const showUploadPanel = hasRelevantFiles && (uploadInProgress || uploadCtx.autoProcess);
    // Defense in depth: even if showUploadPanel's window is somehow missed
    // (e.g. a remount), never allow the manual Extract button to fire a
    // second process-case for a batch UploadContext already auto-triggered.
    const uploadCtxAlreadyProcessing = uploadCtx.caseId === caseId && uploadCtx.processTriggered;

    const renderStage = () => {
        switch (activeStage) {
            /* ── Stage 1: Upload & Manage ── */
            case 'upload':
                return (
                    <div className="stage-single-column stage-upload">
                        <div className="workflow-hint">
                            <span className="workflow-hint-icon">ℹ️</span>
                            <span>Add or remove patient medical history files. You can upload multiple PDF or DOCX files. Once all files are uploaded, extract the medical information to continue.</span>
                        </div>
                        {showUploadPanel ? (
                            <UploadProgressPanel onStartExtraction={handleProcessCase} />
                        ) : (
                            uploadSection
                        )}

                        {resultsStale && (structuredInfo || extractionProgress) && (
                            <div className="stale-banner">
                                <div className="stale-banner-message">
                                    Source documents have changed. Extraction results and generated documents are out of date.
                                </div>
                                {showReExtractConfirm ? (
                                    <div className="confirm-reextract">
                                        <p className="confirm-reextract-warning">
                                            This will erase all existing extracted facts, verifications, and generated documents. They cannot be recovered.
                                        </p>
                                        <div className="confirm-reextract-actions">
                                            <button
                                                className="form-button form-button-danger"
                                                type="button"
                                                onClick={() => handleProcessCase({ confirm: true })}
                                            >
                                                Confirm Re-extraction
                                            </button>
                                            <button
                                                className="form-button form-button-secondary"
                                                type="button"
                                                onClick={() => setShowReExtractConfirm(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        className="form-button"
                                        type="button"
                                        onClick={handleProcessCase}
                                        disabled={isGenerating}
                                    >
                                        {isGenerating ? 'Extracting...' : 'Re-extract Medical Information'}
                                    </button>
                                )}
                            </div>
                        )}

                        {uploadedFiles.length > 0 && !structuredInfo && !extractionProgress && (
                            <button
                                className="form-button"
                                type="button"
                                onClick={handleProcessCase}
                                disabled={isGenerating || uploadCtxAlreadyProcessing}
                            >
                                {uploadCtxAlreadyProcessing ? 'Starting extraction…' : 'Extract Medical Information'}
                            </button>
                        )}
                    </div>
                );

            /* ── Stage 2: Extraction Results ── */
            case 'results':
                return (
                    <div className="stage-single-column stage-results">
                        <div className="workflow-hint">
                            <span className="workflow-hint-icon">ℹ️</span>
                            <span>Review each extracted fact and mark it as correct, or edit it if the value is wrong. All facts must be approved before generating documents.</span>
                        </div>
                        {structuredInfo && (
                            <VerificationBar
                                verificationStats={getVerificationStats()}
                                allVerified={allFactsVerifiedCorrect()}
                                onMarkAllCorrect={handleMarkAllCorrect}
                                onGenerateAll={handleGenerateAll}
                                isGenerating={isGenerating}
                                processingTimedOut={processingTimedOut}
                                hasExistingHPI={!!answer}
                                showReGenerateConfirm={showReGenerateConfirm}
                                onCancelReGenerate={() => setShowReGenerateConfirm(false)}
                                isAdmin={isAdmin}
                                factReviewOnboardingDismissedAt={userProfile?.fact_review_onboarding_dismissed_at}
                                onDismissFactReviewBanner={handleDismissFactReviewBanner}
                            />
                        )}

                        <RiskScoreBanner
                            riskScore={riskScore}
                            recommendation={recommendation}
                        />

                        <RedFlagsAlert
                            redFlags={redFlags}
                            onRedFlagReview={handleRedFlagReview}
                            onMarkAllReviewed={handleMarkAllRedFlagsReviewed}
                        />

                        <FactsPanel
                            groupedFacts={groupedFacts}
                            collapsedGroups={collapsedGroups}
                            onToggleGroup={toggleGroup}
                            extractionProgress={extractionProgress}
                            editableAnswers={editableAnswers}
                            expandedCitations={expandedCitations}
                            onToggleCitation={toggleCitation}
                            onFactVerification={handleFactVerification}
                            onAnswerEdit={handleAnswerEdit}
                            fileCount={uploadedFiles?.length ?? 0}
                        />
                    </div>
                );

            /* ── Coding stage: diagnosis-code review (home_health only) ── */
            case 'coding':
                return (
                    <div className="stage-single-column stage-coding">
                        <DiagnosisCodesPanel
                            diagnosisCodes={mergedDiagnosisCodes}
                            onCodeReview={handleDiagnosisCodeReview}
                            onDesignatePrimary={handleDesignatePrimary}
                            onCopyCodes={handleCopyDiagnosisCodes}
                            copySuccess={copyDxCodesSuccess}
                            onCopyQuery={handleCopyPhysicianQuery}
                            copiedQueryIndex={copiedQueryIndex}
                            onLookupCode={() => setCodeSearchTarget({ mode: 'lookup' })}
                            onRefine={handleRefineCode}
                            onRetryEvidence={handleRetryEvidence}
                        />
                        {codeSearchTarget && (
                            <CodeSearchModal
                                target={codeSearchTarget}
                                onSelect={handleCodeSearchSelect}
                                onClose={() => setCodeSearchTarget(null)}
                                onLookupEvidence={handleLookupEvidence}
                                lookupEvidence={lookupEvidence}
                                caseCodes={codeSearchTarget.mode === 'lookup'
                                    ? new Set((diagnosisCodes?.candidates || [])
                                        .filter(c => c.code && c.status !== 'rejected')
                                        .map(c => c.code))
                                    : undefined}
                            />
                        )}
                    </div>
                );

            /* ── Stage 3: Generated Documents ── */
            case 'documents':
                return (
                    <div className="stage-single-column stage-documents">
                        {documentsStale && (answer || diagnosis) && (
                            <div className="stale-banner">
                                <div className="stale-banner-message">
                                    Extracted facts have changed since these documents were generated. Regenerate documents from the Extraction Results tab to reflect the latest facts.
                                </div>
                            </div>
                        )}

                        {isArchived && (
                            <div className="archived-banner">
                                This case has been archived. PHI data has been removed.
                            </div>
                        )}

                        {/* Top controls: admission status */}
                        <div className="admission-controls">
                            <label htmlFor="admission-status" className="admission-label">Admission Status</label>
                            <select
                                id="admission-status"
                                className="admission-select"
                                value={admissionStatus || ''}
                                onChange={handleAdmissionStatusChange}
                                disabled={isArchived}
                            >
                                <option value="" disabled>Select status...</option>
                                <option value="In progress">In progress</option>
                                <option value="Submitted to Insurance">Submitted to Insurance</option>
                                <option value="Insurance Approved">Insurance Approved</option>
                                <option value="Insurance Denied">Insurance Denied</option>
                                <option value="Rejected">Rejected</option>
                                <option value="Admitted">Admitted</option>
                                <option value="Closed">Closed</option>
                            </select>
                        </div>

                        {/* Sub-tabs for generated documents */}
                        {generatedDocuments ? (
                            <>
                                <div className="tabs">
                                    {sortedDocNames.map((docName) => (
                                        <button
                                            key={docName}
                                            type="button"
                                            className={`tab ${activeTab === docName ? "active" : ""}`}
                                            onClick={() => setActiveTab(docName)}
                                        >
                                            {docName}
                                        </button>
                                    ))}
                                </div>
                                <div className="tab-content">
                                    {sortedDocNames.map((docName) => {
                                        const doc = generatedDocuments[docName];
                                        return activeTab === docName && (
                                            <div key={docName}>
                                                {docName === "Diagnosis" ? (
                                                    <DiagnosisContent
                                                        diagnosis={doc.content}
                                                        onCopy={() => handleCopyGeneratedDoc(docName)}
                                                        copySuccess={copiedDocName === docName}
                                                    />
                                                ) : (
                                                    <HPIContent
                                                        hpiText={doc.content}
                                                        onCopy={() => handleCopyGeneratedDoc(docName)}
                                                        copySuccess={copiedDocName === docName}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="tabs">
                                    <button
                                        type="button"
                                        className={`tab ${activeTab === "hpi" ? "active" : ""}`}
                                        onClick={() => setActiveTab("hpi")}
                                    >
                                        HPI
                                    </button>
                                    <button
                                        type="button"
                                        className={`tab ${activeTab === "diagnosis" ? "active" : ""}`}
                                        onClick={() => setActiveTab("diagnosis")}
                                    >
                                        Diagnosis
                                    </button>
                                </div>
                                <div className="tab-content">
                                    {activeTab === "hpi" && (
                                        <HPIContent
                                            hpiText={answer}
                                            onCopy={handleCopyHPI}
                                            copySuccess={copySuccess}
                                        />
                                    )}
                                    {activeTab === "diagnosis" && (
                                        <DiagnosisContent
                                            diagnosis={diagnosis}
                                            onCopy={handleCopyDiagnosis}
                                            copySuccess={copyDiagnosisSuccess}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    // Build set of stages that should show as completed (green checkmark).
    // A stage is only green when its completion criteria are met.
    // Downstream stages lose green when upstream data changes.
    const completedStages = useMemo(() => {
        const stages = new Set();
        // Upload is done once extraction has started or completed
        if (structuredInfo || extractionProgress) stages.add('upload');
        // Results is done when all facts are verified AND files haven't changed since extraction
        if (allFactsVerifiedCorrect() && !resultsStale) stages.add('results');
        // Documents is done when both HPI and diagnosis are generated and nothing upstream has changed
        if ((answer && diagnosis) && !documentsStale && !resultsStale) stages.add('documents');
        return stages;
    }, [structuredInfo, extractionProgress, answer, diagnosis, documentsStale, resultsStale]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="editcase-wrapper">
            <WorkflowStepper
                currentStage={activeStage}
                progressStage={workflowStage}
                onStageClick={handleStageClick}
                completedStages={completedStages}
                showCodingStep={showCodingStep}
                reachableStages={reachableStages}
            />
            {renderStage()}
        </div>
    );
}

export default EditCase;
